import { track } from "@/utils/conversions";

export const MID_YEAR_CAMPAIGN_SLUG = "mid-year-career-upgrade-2026";
export const MID_YEAR_PRIMARY = "#5c4db1";
export const MID_YEAR_PAGE_PATH = "/upgrade-your-career-with-cosmetic-college";
export const MID_YEAR_START = new Date("2026-05-27T00:00:00+01:00");
/** Original campaign close; extension runs until {@link MID_YEAR_END}. */
export const MID_YEAR_ORIGINAL_END = new Date("2026-06-30T23:59:59+01:00");
/** 48-hour extension from the original close (until 2 July 2026, 23:59 UK). */
export const MID_YEAR_EXTENSION_START = new Date("2026-07-01T00:00:00+01:00");
export const MID_YEAR_END = new Date("2026-07-02T23:59:59+01:00");

export const MID_YEAR_CAMPAIGN_END_LABEL = "2 July 2026";

/** Sponsorship places available during the 48-hour extension window. */
export const MID_YEAR_EXTENSION_ENROLLMENT_LIMIT = 11;

export const MID_YEAR_SPONSORED_STUDENT_LIMIT =
  MID_YEAR_EXTENSION_ENROLLMENT_LIMIT;

export const MID_YEAR_WINDOW_LABEL = `Extended until ${MID_YEAR_CAMPAIGN_END_LABEL}`;

export const MID_YEAR_WINDOW_BADGE = `${MID_YEAR_WINDOW_LABEL} - final ${MID_YEAR_SPONSORED_STUDENT_LIMIT} sponsorship places`;

export const MID_YEAR_DISCOUNT_CODE = "SPONSOR30";

export const MID_YEAR_SPONSORSHIP_PRICE_NOTE = `30% sponsorship credit - use ${MID_YEAR_DISCOUNT_CODE} at checkout`;

/** Buy 2 VTCT Level 3 massage courses, get the third free (all 3 in basket). */
export const MID_YEAR_MASSAGE_BUNDLE_DISCOUNT_CODE = "L3MASSAGE";

export const MID_YEAR_MASSAGE_BUNDLE_PRICE_NOTE = `Buy 2, get 1 free - use ${MID_YEAR_MASSAGE_BUNDLE_DISCOUNT_CODE} at checkout`;

export const MID_YEAR_MASSAGE_SPONSORSHIP_PRICE_NOTE = [
  MID_YEAR_SPONSORSHIP_PRICE_NOTE,
  "Buy 2 get 1 free on all 3 with L3MASSAGE",
] as const;

export const MID_YEAR_MASSAGE_COURSE_IDS = [
  "65e32d36-49c6-4fb6-9cca-a82bb39f9a3e",
  "47807413-bd73-4572-b370-5e8c8543ad38",
  "1a08a347-1b81-44b5-b750-42f846d5d0b3",
] as const;

export const MID_YEAR_HEADLINE =
  "Make 2026 the Year You Achieve Your Career Goals";

export const MID_YEAR_EVENT_NAME = "Mid-Year Career Upgrade Event";

export const MID_YEAR_NARRATIVE_LEDE =
  `The Mid-Year Career Upgrade is extended for 48 hours - final ${MID_YEAR_SPONSORED_STUDENT_LIMIT} sponsorship places on selected programmes.`;

export const MID_YEAR_HERO_BODY =
  `Until ${MID_YEAR_CAMPAIGN_END_LABEL}, or when the final ${MID_YEAR_SPONSORED_STUDENT_LIMIT} places are taken, selected programmes may receive a sponsored training contribution of up to 30% of course fees, plus First Aid & Anaphylaxis training (£499 value), priority assessment turnaround and RPL routes from £999 + VAT.`;

export const MID_YEAR_PROMO_BODY =
  `Extended 48 hours: final ${MID_YEAR_SPONSORED_STUDENT_LIMIT} sponsorship places with training contributions on selected programmes, plus First Aid & Anaphylaxis, priority turnaround and RPL routes from £999 + VAT.`;

export const MID_YEAR_BANNER_MOBILE =
  `Mid-Year Career Upgrade extended - final ${MID_YEAR_SPONSORED_STUDENT_LIMIT} places`;

export const MID_YEAR_BANNER_DESKTOP =
  `Mid-Year Career Upgrade extended until ${MID_YEAR_CAMPAIGN_END_LABEL} - final ${MID_YEAR_SPONSORED_STUDENT_LIMIT} sponsorship places`;

export const MID_YEAR_META_DESCRIPTION =
  `Mid-Year Career Upgrade extended until ${MID_YEAR_CAMPAIGN_END_LABEL}: final ${MID_YEAR_SPONSORED_STUDENT_LIMIT} sponsorship places with up to 30% training contributions on selected programmes, plus First Aid & Anaphylaxis, priority turnaround and RPL from £999 + VAT.`;

export const MID_YEAR_OFFER_STACK = [
  {
    id: "sponsorship",
    title: "Sponsored training contribution",
    detail:
      `Only ${MID_YEAR_SPONSORED_STUDENT_LIMIT} sponsored student places remain on selected programmes. Cosmetic College contributes up to 30% of your course fees toward your training.`,
    highlight: `Final ${MID_YEAR_SPONSORED_STUDENT_LIMIT} places`,
  },
  {
    id: "first-aid",
    title: "First Aid & Anaphylaxis included",
    detail:
      "Eligible sponsored enrolments receive our First Aid & Anaphylaxis training package - a £499 value included to support your readiness.",
    highlight: "£499 included",
  },
  {
    id: "priority",
    title: "Priority turnaround",
    detail:
      "Faster assessment and certificate processing for sponsored event enrolments.",
    highlight: "Move faster",
  },
  {
    id: "massage-trio",
    title: "VTCT Level 3 massage trio",
    detail:
      "Buy two of our three Level 3 massage awards and get the third free. Add all three to basket and apply L3MASSAGE at checkout.",
    highlight: "Buy 2 get 1 free",
  },
  {
    id: "rpl",
    title: "RPL routes from £999 + VAT",
    detail:
      "Recognition of Prior Learning for eligible experienced practitioners.",
    highlight: "Experienced route",
  },
  {
    id: "online-case-study",
    title: "Online learning with tutor-reviewed case studies",
    detail:
      "Study on demand, then submit structured portfolio work for tutor feedback - often within five working days. Compare theory-only and tutor-reviewed tiers from £99.",
    highlight: "New online pathway",
  },
];

export type MidYearFeaturedProgramme = {
  id: string;
  badge: string;
  title: string;
  description: string;
  priceNote: string | string[];
  href: string;
  purchaseHref: string;
  image: string;
  imageAlt: string;
  courseId?: string;
  courseIds?: string[];
  primaryCtaLabel?: string;
  /** Omit to apply MID_YEAR_DISCOUNT_CODE at checkout; set null to skip. */
  discountCode?: string | null;
};

export const AESTHETIC_CAREER_ACCELERATOR_EVENT_DETAIL_ID =
  "dd244d00-bbe4-49e4-9bd2-68c6379e3525";

export const MID_YEAR_FEATURED_PROGRAMMES: MidYearFeaturedProgramme[] = [
  {
    id: "level-7",
    badge: "Highest impact",
    title:
      "VTCT Level 7 Diploma in Non-Surgical Aesthetic Injectable Procedures",
    description:
      "The regulated route for eligible practitioners building a serious injectables career.",
    priceNote: "£4,000 on summer dates",
    href: "/courses/vtct/level-7-diploma-in-non-surgical-aesthetic-injectable-procedures/",
    purchaseHref:
      "/courses/vtct/level-7-diploma-in-non-surgical-aesthetic-injectable-procedures/#course-dates",
    courseId: "3eac27ac-57d4-4e5d-b50b-e5531c416288",
    image:
      "https://s3.eu-west-2.amazonaws.com/assets.cosmetic.college/cms/4XMIFbp9EgevoLdtvEfwTF/mesotherapy.jpg",
    imageAlt: "Level 7 injectables training",
  },
  {
    id: "level-5-skin",
    badge: "Skin rejuvenation",
    title:
      "VTCT Level 5 Certificate in Non-Surgical Aesthetic Procedures for Skin",
    description:
      "Advance your skin treatment offering with a recognised Level 5 qualification.",
    priceNote: MID_YEAR_SPONSORSHIP_PRICE_NOTE,
    href: "/courses/vtct/level-5-certificate-in-non-surgical-aesthetic-procedures-for-skin/",
    purchaseHref:
      "/courses/vtct/level-5-certificate-in-non-surgical-aesthetic-procedures-for-skin/#course-dates",
    courseId: "80d5c0f9-9937-4e38-b1f6-8ba2b2b403e3",
    image:
      "https://s3.eu-west-2.amazonaws.com/assets.cosmetic.college/cms/4sQUUdOQljK83lkQccsB2Z/dermaplaning_pic_.jpg",
    imageAlt: "Level 5 skin rejuvenation training",
  },
  {
    id: "level-3-access",
    badge: "Foundation route",
    title: "VTCT Level 3 Certificate in Access to Aesthetic Therapies",
    description:
      "The recognised entry route into regulated aesthetics - ideal if you are starting out or moving from beauty into clinical treatments.",
    priceNote: MID_YEAR_SPONSORSHIP_PRICE_NOTE,
    href: "/courses/vtct/level-3-certificate-in-access-to-aesthetic-therapies/",
    purchaseHref:
      "/courses/vtct/level-3-certificate-in-access-to-aesthetic-therapies/#course-dates",
    courseId: "80d59648-2fc7-4ea4-b268-029aaf47e6a4",
    image:
      "https://s3.eu-west-2.amazonaws.com/assets.cosmetic.college/cms/6FlTABBmzpWnj0IUbhPwbe/VTCT_FACIAL_MASSAGE.jpg",
    imageAlt: "VTCT Level 3 Access to Aesthetic Therapies training",
  },
  {
    id: "level-3-body-massage",
    badge: "Massage",
    title: "VTCT Level 3 Award in Body Massage",
    description:
      "Build practical massage skills and client-care confidence with a nationally recognised qualification for beauty and spa work.",
    priceNote: [...MID_YEAR_MASSAGE_SPONSORSHIP_PRICE_NOTE],
    href: "/courses/vtct/level-3-award-in-body-massage/",
    purchaseHref: "/courses/vtct/level-3-award-in-body-massage/#course-dates",
    courseId: "65e32d36-49c6-4fb6-9cca-a82bb39f9a3e",
    image:
      "https://s3.eu-west-2.amazonaws.com/assets.cosmetic.college/cms/4S1EBuuXMVlln0dUUzOXH6/vtct-level-3-body-massage-cosmetic-college.jpeg",
    imageAlt: "VTCT Level 3 Body Massage training",
  },
  {
    id: "level-3-indian-head-massage",
    badge: "Massage",
    title: "VTCT Level 3 Award in Indian Head Massage",
    description:
      "Add a high-demand salon and spa treatment with VTCT-accredited Indian head massage training.",
    priceNote: [...MID_YEAR_MASSAGE_SPONSORSHIP_PRICE_NOTE],
    href: "/courses/vtct/level-3-award-in-indian-head-massage/",
    purchaseHref: "/courses/vtct/level-3-award-in-indian-head-massage/#course-dates",
    courseId: "47807413-bd73-4572-b370-5e8c8543ad38",
    image:
      "https://s3.eu-west-2.amazonaws.com/assets.cosmetic.college/cms/6e411mUnQB2Mtvq3Cm4yPu/vtct-level-3-indian-head-massage.jpg",
    imageAlt: "VTCT Level 3 Indian Head Massage training",
  },
  {
    id: "level-3-aromatherapy-massage",
    badge: "Massage",
    title: "VTCT Level 3 Award in Massage Using Pre-Blended Aromatherapy Oils",
    description:
      "Learn to deliver professional aromatherapy massage treatments with pre-blended oils and recognised VTCT certification.",
    priceNote: [...MID_YEAR_MASSAGE_SPONSORSHIP_PRICE_NOTE],
    href: "/courses/vtct/level-3-award-in-massage-using-pre-blended-aromatherapy-oils/",
    purchaseHref:
      "/courses/vtct/level-3-award-in-massage-using-pre-blended-aromatherapy-oils/#course-dates",
    courseId: "1a08a347-1b81-44b5-b750-42f846d5d0b3",
    image:
      "https://s3.eu-west-2.amazonaws.com/assets.cosmetic.college/cms/5pKInY2YqmCLUndVevOoCt/vtct-level-3-aromatherapy-massage-training-course.jpg",
    imageAlt: "VTCT Level 3 aromatherapy massage training",
  },
  {
    id: "massage-trio-bundle",
    badge: "Buy 2 get 1 free",
    title: "VTCT Level 3 Massage Trio Bundle",
    description:
      "Take all three Level 3 massage awards - body massage, Indian head massage and pre-blended aromatherapy - and get one course free when you checkout with all three in your basket.",
    priceNote: MID_YEAR_MASSAGE_BUNDLE_PRICE_NOTE,
    href: `${MID_YEAR_PAGE_PATH}#massage-trio`,
    purchaseHref: `${MID_YEAR_PAGE_PATH}#massage-trio`,
    courseIds: [...MID_YEAR_MASSAGE_COURSE_IDS],
    discountCode: MID_YEAR_MASSAGE_BUNDLE_DISCOUNT_CODE,
    primaryCtaLabel: "Add all 3 to basket",
    image:
      "https://s3.eu-west-2.amazonaws.com/assets.cosmetic.college/cms/4S1EBuuXMVlln0dUUzOXH6/vtct-level-3-body-massage-cosmetic-college.jpeg",
    imageAlt: "VTCT Level 3 massage trio bundle",
  },
  {
    id: "online-case-study",
    badge: "New online pathway",
    title: "Online Learning With Tutor-Reviewed Case Studies",
    description:
      "Study on demand, then submit structured portfolio work for tutor feedback - often within five working days. Compare theory-only and tutor-reviewed tiers on each course card before you enrol.",
    priceNote: "From £99 · same-day access on eligible courses",
    href: "/courses/elearning/",
    purchaseHref: "/courses/elearning/pathways/case-study/",
    image:
      "https://s3.eu-west-2.amazonaws.com/assets.cosmetic.college/cms/h00yNsDwQGGuLFMIpodEu/Image_from_iOS-25.jpg",
    imageAlt: "Online training with tutor-reviewed case studies",
  },
  {
    id: "laser",
    badge: "Clinic staple",
    title: "VTCT Level 4 Laser Hair Removal & IPL",
    description:
      "Add high-demand laser and light treatments with VTCT-accredited training.",
    priceNote: MID_YEAR_SPONSORSHIP_PRICE_NOTE,
    href: "/courses/vtct/laser-hair-removal/",
    purchaseHref: "/courses/vtct/laser-hair-removal/#course-dates",
    courseId: "ab170dd8-6132-4873-9392-f9cf87f114c0",
    image:
      "https://s3.eu-west-2.amazonaws.com/assets.cosmetic.college/cms/7D20Uc0mfHyhTVo3tGIvdA/laser-tattoo-removal-cosmetic-college.jpg",
    imageAlt: "Laser hair removal training",
  },
  {
    id: "cpd-package",
    badge: "Build your own",
    title: "Build Your Own 4-Course CPD Package",
    description:
      "Choose four onsite CPD courses from our practical catalogue and checkout at a fixed package price.",
    priceNote: "£999 excl. VAT · pick any 4 eligible CPD courses",
    href: "/build-your-cpd-package/",
    purchaseHref: "/build-your-cpd-package/",
    primaryCtaLabel: "Build your package",
    image:
      "https://s3.eu-west-2.amazonaws.com/assets.cosmetic.college/cms/1BHmEBy9fH4X8ZXE1SD4PP/shutterstock_2105495051.jpg",
    imageAlt: "Build your own CPD training package",
  },
  {
    id: "fast-track",
    badge: "Career accelerator",
    title: "5-Day Aesthetic Career Accelerator",
    description:
      "Fast-track your aesthetics career with online pre-study, five intensive practical days and 14 CPD certificates.",
    priceNote: "£1,250",
    href: "/aesthetic-career-accelerator/",
    purchaseHref: "/aesthetic-career-accelerator/",
    courseId: AESTHETIC_CAREER_ACCELERATOR_EVENT_DETAIL_ID,
    discountCode: null,
    image: "/shots/fast-track.png",
    imageAlt: "5-Day Aesthetic Career Accelerator training",
  },
  {
    id: "rpl",
    badge: "Experienced practitioners",
    title: "Recognition of Prior Learning (RPL)",
    description:
      "Already qualified or experienced? Explore RPL pathways from £999 + VAT.",
    priceNote: "From £999 + VAT",
    href: "/courses/rpl/",
    purchaseHref: "/courses/rpl/",
    image:
      "https://s3.eu-west-2.amazonaws.com/assets.cosmetic.college/cms/4XMIFbp9EgevoLdtvEfwTF/mesotherapy.jpg",
    imageAlt: "RPL aesthetics pathways",
  },
];

export const MID_YEAR_TESTIMONIALS = [
  {
    quote:
      "I would highly recommend Cosmetic College. I have attended two of their courses now and will continue to do so.",
    author: "Stacy Fuller",
  },
  {
    quote:
      "Such a fantastic course with an amazing tutor Charlotte. I was made to feel so welcome and supported throughout. The pace of the course was perfect, and I feel ready to start treatments.",
    author: "Claire Surridge",
  },
];

export function isMidYearCampaignActive(at = new Date()) {
  return at >= MID_YEAR_START && at <= MID_YEAR_END;
}

/** Site-wide promo strip during the 48-hour extension window. */
export function isMidYearPromotionVisible(at = new Date()) {
  return at >= MID_YEAR_EXTENSION_START && at <= MID_YEAR_END;
}

export function buildMidYearCallbackPath(source = MID_YEAR_CAMPAIGN_SLUG) {
  return `/request-a-callback/?source=${encodeURIComponent(source)}`;
}

export function buildMidYearCheckoutPath(
  discountCode: string | null | undefined = MID_YEAR_DISCOUNT_CODE,
) {
  if (!discountCode) {
    return "/checkout";
  }

  return `/checkout?code=${encodeURIComponent(discountCode)}`;
}

export function buildMidYearQuizPath(campaign = MID_YEAR_CAMPAIGN_SLUG) {
  const params = new URLSearchParams();
  params.set("campaign", campaign);
  return `/best-aesthetic-course-for-you/?${params.toString()}`;
}

export function isMidYearCampaign(value?: string | string[] | null) {
  if (!value) {
    return false;
  }

  const testValue = Array.isArray(value) ? value[0] : value;
  return testValue === MID_YEAR_CAMPAIGN_SLUG;
}

export function getMidYearCountdown(at = new Date()) {
  const target = MID_YEAR_END.getTime();
  const now = at.getTime();
  const distance = target - now;

  if (distance <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, active: false };
  }

  return {
    days: Math.floor(distance / (1000 * 60 * 60 * 24)),
    hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((distance % (1000 * 60)) / 1000),
    active: true,
  };
}

export function trackMidYearEvent(
  action: string,
  params: Record<string, unknown> = {},
) {
  if (typeof window === "undefined") {
    return;
  }

  track(action, {
    event_category: "Mid-Year Career Upgrade",
    event_label: MID_YEAR_CAMPAIGN_SLUG,
    ...params,
  });

}
